import { User, UserDetails, UserNavOverrides } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { USER_DETAILS_PATH } from '@/consts/client';
import { loadable } from '@/utils/jotai';
import { objSnakeToCamel } from '@/utils/queries';
import { fetchClientAtom } from './client';

export const rawUserDetailsAtom = atomWithSuspenseQuery(get => {
  const queryKey = ['user'] as const;
  const queryFn = async () => {
    // This particular atom sometimes triggers the query before the fetch client has been initialised,
    // so we need to make sure we get this inside the query function
    const { fetch } = get(fetchClientAtom);
    const response = await fetch(USER_DETAILS_PATH);
    return response as UserDetails;
  };
  return { queryKey, queryFn };
});

export const userDetailsAtom = atom(async get => {
  const rawUserDetails = await get(rawUserDetailsAtom);
  return objSnakeToCamel(rawUserDetails.data);
});

export const hudlUserIdAtom = atom(async get => (await get(userDetailsAtom)).user.hudlUserId);

export const unwrappedHudlUserIdAtom = unwrap(hudlUserIdAtom, prev => prev);

export const loadableUserDetailsAtom = loadable(rawUserDetailsAtom);

export const entityUsersAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['entityUsers'] as const;
  const queryFn = async () => {
    const response = await fetch(`/entity/users`);
    return response as User[];
  };
  return { queryKey, queryFn };
});

export const hudlUserIdUserNameMapAtom = atom(async get => {
  const users = await get(entityUsersAtom);
  return users.data.reduce<Record<string, string>>((acc, user) => {
    acc[user.hudl_user_id] = user.name;
    return acc;
  }, {});
});

export const rawUserNavOverridesAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['userNavOverrides'] as const;
  const queryFn = async () => {
    const response = await fetch(`/user/nav-overrides`);
    return response as UserNavOverrides;
  };

  return { queryKey, queryFn };
});

export const userNavOverridesAtom = atom(async get => {
  const userNavOverrides = await get(rawUserNavOverridesAtom);
  return objSnakeToCamel(userNavOverrides.data);
});

export const unwrappedUserNavOverridesAtom = unwrap(
  userNavOverridesAtom,
  prev => prev || { player: false, team: false, playerScout: false },
);
